import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Sparkles, Tag } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import type { ServiceCategory, ServiceItem } from "@/lib/services";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildServiceListSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/pricing")({
  component: PricingPage,
  loader: async (): Promise<{
    content: PageContent;
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  }> => {
    const [{ PRICING_CONTENT }, { SERVICES, CATEGORIES }] = await Promise.all([
      import("@/content/site-pages"),
      import("@/lib/services"),
    ]);
    return { content: PRICING_CONTENT, services: SERVICES, categories: CATEGORIES };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `ราคาบริการ Notary / แปล / กงสุล ใน${config.nameTh} — Thai Notary`;
    const description = `ราคา Notary Public ${config.nameTh} 1,500–8,000 บาท · แปลรับรอง 500 บาท/หน้า · NAATI 1,300 บาท · MFA 2,500–5,000 · สถานทูต 5,000–12,000 — โปร่งใส ไม่มีค่าซ่อน`;
    const head = pageHeadForProvince({ title, description, path: "/pricing" });
    const priced = (loaderData?.services ?? []).filter((s) => s.priceFrom).map((s) => ({
      name: s.title,
      description: s.desc,
      url: `/services/${s.slug}`,
      priceFrom: s.priceFrom,
    }));
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/pricing")) },
        { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ราคา", url: `${site}/pricing` },
            ]),
          ),
        },
        ...buildServiceListSchema(priced).map((s) => ({
          type: "application/ld+json",
          children: JSON.stringify(s),
        })),
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(loaderData?.content.faqs ?? [])),
        },
      ],
    };
  },
});

function formatPrice(s: { priceFrom?: number; priceTo?: number; unit?: string }) {
  if (!s.priceFrom) return "ติดต่อเพื่อใบเสนอราคา";
  const from = s.priceFrom.toLocaleString();
  const to = s.priceTo ? `–${s.priceTo.toLocaleString()}` : "+";
  return `฿${from}${to}${s.unit ? ` ${s.unit}` : ""}`;
}

function PricingPage() {
  const { content, services: SERVICES, categories: CATEGORIES } = Route.useLoaderData() as {
    content: PageContent;
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  };
  const { config } = getActiveProvince();
  const grouped: Record<ServiceCategory, ServiceItem[]> = {
    notary: [],
    translation: [],
    legalization: [],
    background: [],
    "visa-marriage": [],
  };
  for (const s of SERVICES) grouped[s.category].push(s);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Tag className="h-3.5 w-3.5" /> ราคาโปร่งใส · ไม่มีค่าซ่อน
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              ราคาบริการใน{config.nameTh}
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-muted-foreground">
              ค่าบริการตามมาตรฐานสภาทนายความฯ — ราคาเดียวทั่วประเทศ ไม่มี premium พิเศษ
              สำหรับ{config.nameTh} (มีเพิ่มเฉพาะค่าเดินทาง on-site)
            </p>
          </div>
        </section>

        {(Object.keys(grouped) as ServiceCategory[]).map((cat) => {
          const items = grouped[cat];
          if (!items.length) return null;
          return (
            <section key={cat} className="border-b border-border">
              <div className="mx-auto max-w-5xl px-4 py-12">
                <h2 className="mb-6 flex items-center gap-2 text-2xl font-bold md:text-3xl">
                  <Sparkles className="h-5 w-5 text-gold" />
                  {CATEGORIES.find((c) => c.id === cat)?.label ?? cat}
                </h2>
                <div className="overflow-hidden rounded-xl border border-border">
                  <table className="w-full text-left text-sm">
                    <thead className="hidden bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground sm:table-header-group">
                      <tr>
                        <th className="px-3 py-3 sm:px-4">บริการ</th>
                        <th className="whitespace-nowrap px-3 py-3 text-right sm:px-4">ราคาเริ่มต้น</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((s) => (
                        <tr key={s.id} className="block border-t border-border align-top sm:table-row">
                          <td className="block px-4 pb-1 pt-3 sm:table-cell sm:py-3">
                            <Link
                              to="/services/$service"
                              params={{ service: s.slug }}
                              className="font-semibold text-foreground hover:text-primary"
                            >
                              {s.title}
                            </Link>
                            <div className="mt-1 text-xs text-muted-foreground">{s.desc}</div>
                          </td>
                          <td className="block px-4 pb-3 pt-0 text-left font-bold text-primary sm:table-cell sm:whitespace-nowrap sm:py-3 sm:text-right">
                            {formatPrice(s)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          );
        })}

        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="mb-4 text-xl font-bold md:text-2xl">รวมอยู่ในราคาทุกรายการ</h2>
            <ul className="grid gap-2 md:grid-cols-2">
              {[
                "ตอบกลับ LINE ภายใน 30 นาที",
                "ใบเสนอราคาฟรี ไม่มีค่ามัดจำ",
                `รับเอกสารถึงสาขาในเขตเมือง${config.nameTh} ฟรี`,
                "แก้ไขงานแปลฟรีจนกว่าจะ approve",
                "ปรึกษาทนายฟรี 15 นาทีก่อนเริ่มงาน",
                "ใบเสร็จ/ใบกำกับภาษีถูกต้องตามกฎหมาย",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <LongFormContent content={content} />

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-xl font-bold md:text-2xl">ต้องการใบเสนอราคาเฉพาะกรณีของคุณ?</h2>
            <p className="mt-2 text-sm opacity-90">ส่งรายละเอียดเอกสารทาง LINE — ทีม{config.nameTh}ประเมินให้ฟรีใน 30 นาที</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-3 text-sm font-bold text-gold-foreground">
                📞 {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-primary-foreground/40 px-5 py-3 text-sm font-bold"
              >
                LINE {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}